import React, { useState } from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { LogoMark } from "@/components/brand";
import AmbientSound from "@/components/AmbientSound";
import { ContactLine } from "@/components/site/ContactLine";
import GhostLauncher from "@/components/ghost/GhostLauncher";
import InstallApp from "@/components/InstallApp";
import { useAuth } from "@/context/AuthContext";
import { SIGNATURE } from "@/constants/media";
import api from "@/lib/api";

const NAV = [
  { to: "/experiences", label: "Experiences" },
  { to: "/membership", label: "Membership" },
  { to: "/gallery", label: "Gallery" },
  { to: "/resort-guide", label: "Resort Guide" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const FOOTER_NAV = [
  { to: "/experiences", label: "The Experience" },
  { to: "/book", label: "Reservations" },
  { to: "/experiences", label: "Accommodations" },
  { to: "/experiences", label: "Pool & Social Experiences" },
  { to: "/private-events", label: "Private Events" },
  { to: "/membership", label: "Membership" },
  { to: "/gallery", label: "Gallery" },
  { to: "/resort-guide", label: "Resort Guide" },
  { to: "/policies", label: "Policies" },
  { to: "/contact", label: "Contact" },
  { to: "/login", label: "Guest Login" },
  { to: "/manage", label: "Manage Reservation" },
];

const FOOTER_LEGAL = [
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/terms", label: "Terms and Conditions" },
  { to: "/cancellation-policy", label: "Cancellation Policy" },
  { to: "/payment-policy", label: "Payment Policy" },
  { to: "/accessibility", label: "Accessibility" },
];

export default function PublicLayout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [prestigiousUrl, setPrestigiousUrl] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    api.get("/content/settings").then((r) => setPrestigiousUrl(r.data.prestigious_url || "")).catch(() => {});
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-ink text-foreground">
      <header className={`fixed top-0 inset-x-0 z-40 transition-colors duration-500 ${scrolled ? "glass-dark" : "bg-transparent"}`}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="nav-home-logo"><LogoMark className="h-11 w-11" withText size="text-xl" /></Link>
          <nav className="hidden lg:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `text-sm tracking-label uppercase transition-colors ${isActive ? "text-gold" : "text-foreground/70 hover:text-gold"}`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3">
            {user ? (
              <button onClick={() => nav(user.is_staff ? "/admin" : "/portal")} data-testid="nav-account-btn"
                className="btn-ghost-gold px-4 py-2 text-sm flex items-center gap-2"><User size={15} />{user.is_staff ? "Command Center" : "My Account"}</button>
            ) : (
              <button onClick={() => nav("/login")} data-testid="nav-login-btn" className="text-sm tracking-label uppercase text-foreground/70 hover:text-gold">Guest Login</button>
            )}
            <button onClick={() => nav("/book")} data-testid="nav-book-btn" className="btn-gold px-5 py-2.5 text-sm">Reserve</button>
          </div>
          <button className="lg:hidden text-gold" onClick={() => setOpen(true)} data-testid="nav-mobile-open"><Menu /></button>
        </div>
      </header>

      {open && (
        <div className="fixed inset-0 z-50 bg-ink/95 backdrop-blur-xl lg:hidden flex flex-col p-6" data-testid="mobile-menu">
          <div className="flex items-center justify-between mb-10">
            <LogoMark className="h-10 w-10" withText size="text-lg" />
            <button onClick={() => setOpen(false)} className="text-gold" data-testid="nav-mobile-close"><X /></button>
          </div>
          <nav className="flex flex-col gap-6">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="font-serif text-3xl text-foreground/90 hover:text-gold">{n.label}</Link>
            ))}
          </nav>
          <div className="mt-auto flex flex-col gap-3">
            <button onClick={() => { setOpen(false); nav(user ? (user.is_staff ? "/admin" : "/portal") : "/login"); }} className="btn-ghost-gold py-3">{user ? "My Account" : "Guest Login"}</button>
            <button onClick={() => { setOpen(false); nav("/book"); }} className="btn-gold py-3">Reserve Your Experience</button>
          </div>
        </div>
      )}

      <main><Outlet /></main>

      <footer className="relative border-t border-gold/15 mt-24 bg-surface/40" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <LogoMark className="h-14 w-14" withText size="text-2xl" />
              <p className="mt-6 text-sm text-muted-foreground max-w-sm leading-relaxed">
                An invitation-only private resort and social house. Arrive as a guest. Leave as part of The D.
              </p>
              <p className="mt-6 font-serif italic text-gold text-lg">NEVER FORGET THE DIFFERENCE YOU MAKE</p>
              <p className="mt-6 text-xs uppercase tracking-label text-gold">Contact</p>
              <p className="text-sm"><ContactLine /></p>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-label text-gold mb-4">Explore</h4>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                {FOOTER_NAV.map((n, i) => <li key={i}><Link className="hover:text-gold transition-colors" to={n.to}>{n.label}</Link></li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-label text-gold mb-4">Legal</h4>
              <ul className="space-y-2.5 text-sm text-muted-foreground">
                {FOOTER_LEGAL.map((n) => <li key={n.to}><Link className="hover:text-gold transition-colors" to={n.to}>{n.label}</Link></li>)}
              </ul>
            </div>
          </div>
          <div className="gold-line my-10" />
          <div className="flex flex-col items-center text-center gap-2">
            <div className="font-serif text-gold tracking-brand text-lg">THE D</div>
            <div className="text-[10px] tracking-label uppercase text-muted-foreground">Private Resort &amp; Social House</div>
            <p className="text-xs text-muted-foreground mt-3" data-testid="footer-attribution">
              {prestigiousUrl
                ? <>Site built and managed by <a href={prestigiousUrl} target="_blank" rel="noopener noreferrer" className="text-gold hover:underline">Prestigious Companies</a>.</>
                : <>Site built and managed by <span className="text-foreground/80">Prestigious Companies</span>.</>}
            </p>
            <p className="text-xs text-muted-foreground">© {new Date().getFullYear()} The D. All rights reserved.</p>
            <div className="mt-3"><InstallApp variant="link" /></div>
          </div>
        </div>
      </footer>
      <AmbientSound />
      <GhostLauncher mode="public" />
    </div>
  );
}
